import React, { useState, useEffect, useContext } from "react";
import {
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
  Tooltip,
} from "@mui/material";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { isEmpty } from "lodash";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";

import NoData from "../../../components/NoData";
import SearchField from "../../../components/ui/SearchField";
import ListFooter from "../../../components/ui/ListFooter";
import StatusChip from "../../../components/ui/StatusChip";
import { getRolListAsync } from "../../../api/Rols";
import SmallModal from "../../../components/modals/SmallModal";
import EditServerRolAccess from "./EditServerRolAccess";
import moment from "moment/moment";
import { getTokens } from "../../../theme";

const ServerRollist = () => {
  const [rolList, setRolList] = useState([]);
  const [selectedRol, setSelectedRol] = useState([]);

  const [localReload, setLocalReload] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const { setIsLoading, setIsUnautorized, access, isDarkMode } = useContext(DataContext);
  const t = getTokens(isDarkMode);

  const token = getToken();

  const [searchTerm, setSearchTerm] = useState("");

  const withSearch = rolList.filter((val) => {
    if (searchTerm === "") {
      return val;
    } else if (
      val.id.toString().includes(searchTerm) ||
      (val.rolName || "").toString().includes(searchTerm)
    ) {
      return val;
    }
    return false;
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const result = await getRolListAsync(token);

      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setIsLoading(false);
      setRolList(result.data.result);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localReload]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = withSearch.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const onChangeSearch = (val) => {
    setCurrentPage(1);
    setSearchTerm(val.toUpperCase());
    paginate(1);
  };

  const setHora = (horaString) => {
    if (horaString !== null) {
      let dateString = moment().format("YYYY-MM-DD").toString();
      let datehoraString = `${dateString}T${horaString}`;
      return new Date(datehoraString);
    }
    return new Date();
  };

  const siNo = (valor) => (
    <StatusChip label={valor ? "SI" : "NO"} tone={valor ? "success" : "neutral"} />
  );

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <SearchField value={searchTerm} onChange={onChangeSearch} placeholder="Buscar rol…" />
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Nombre del rol</TableCell>
                    <TableCell align="center">Desde</TableCell>
                    <TableCell align="center">Hasta</TableCell>
                    <TableCell align="center">Sábado</TableCell>
                    <TableCell align="center">Domingo</TableCell>
                    <TableCell align="center">24/7</TableCell>
                    {isAccess(access, "SECURITY UPDATE") ? (
                      <TableCell align="center">Editar horario</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        {row.id}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.rolName}</TableCell>

                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {row.activoHasta === null ? "" : moment(setHora(row.activoDesde)).format("hh:mm A")}
                      </TableCell>

                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {row.activoHasta === null ? "" : moment(setHora(row.activoHasta)).format("hh:mm A")}
                      </TableCell>

                      <TableCell align="center">{siNo(row.trabajarSabado)}</TableCell>
                      <TableCell align="center">{siNo(row.trabajarDomingo)}</TableCell>
                      <TableCell align="center">{siNo(row.isAccess247)}</TableCell>

                      {isAccess(access, "SECURITY UPDATE") ? (
                        <TableCell align="center">
                          <Tooltip title="Editar horario">
                            <IconButton
                              size="small"
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "9px",
                                border: `1px solid ${t.fieldLine}`,
                                backgroundColor: t.field,
                                color: "warning.main",
                              }}
                              onClick={() => {
                                setSelectedRol(row);
                                setEditModal(true);
                              }}
                            >
                              <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={withSearch}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="roles"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={`Editar Horario Rol: ${selectedRol.rolName}`}
        isVisible={editModal}
        setVisible={setEditModal}
      >
        <EditServerRolAccess
          selectedRol={selectedRol}
          setShowModal={setEditModal}
          localReload={localReload}
          setLocalReload={setLocalReload}
        />
      </SmallModal>
    </div>
  );
};

export default ServerRollist;
